import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { 
  Link,
  Users,
  Gauge,
  TrendingUp,
  ArrowRight
} from 'lucide-react';
import HeroImage from '../components/HeroImage';
import FeatureCard from '../components/FeatureCard';
import TestimonialCard from '../components/TestimonialCard';
import FaqItem from '../components/FaqItem';

const GOOGLE_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLScVJ5NP38k3xJuhVYWnZSFm8PKrzA6Dqu7U73X9fdCI5A2PtQ/viewform?usp=header";

function Home() {
  return (
    <div className="relative">
      {/* Hero Section */}
      <section className="relative h-screen mx-[4.2mm] mt-[4.2mm]">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#00ffff]/10 via-transparent to-[#ff0000]/10 animate-gradient rounded-lg z-10"></div>
          <div className="absolute inset-0 rounded-lg overflow-hidden">
            <HeroImage 
              src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=1920&h=1080" 
              alt="Digital network visualization"
              priority={true}
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50 rounded-lg z-20"></div>
        </div>

        <div className="relative h-full flex items-center z-30">
          <div className="max-w-7xl mx-auto px-4 text-center md:pt-0 pt-36">
            <h1 className="text-4xl sm:text-5xl md:text-[6rem] font-bold mb-2 hero-text leading-tight">
              We Get Results.
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl hero-text-secondary mb-8 max-w-3xl mx-auto px-4">
              We develop innovative solutions to carry your business into the future. In other words, <strong>we do the heavy lifting</strong>.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4 px-4">
              <a
                href={GOOGLE_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 sm:px-8 py-3 sm:py-4 border-2 border-[#00ffff] text-[#00ffff] rounded-md hover:bg-[#ff0000] hover:border-[#ff0000] hover:text-white transition-colors text-lg sm:text-xl font-semibold"
              >
                Get Started
              </a>
              <RouterLink 
                to="/about"
                className="px-6 sm:px-8 py-3 sm:py-4 border-2 border-[#ff0000] text-[#ff0000] rounded-md hover:bg-[#00ffff] hover:border-[#00ffff] hover:text-white transition-colors text-lg sm:text-xl font-semibold"
              >
                Learn More
              </RouterLink>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Atlas Section */}
      <section id="why-atlas" className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-6 sm:mb-8 text-white">Why Choose Atlas?</h2>
          <p className="text-lg sm:text-xl md:text-2xl text-white/80 text-center max-w-4xl mx-auto mb-12 sm:mb-16 px-4">
            At Atlas AI Business Solutions, we combine Speed, Ingenuity, and Expertise with the power of AI to solve your toughest business challenges. Our services cover everything from the basic foundations of building a business, to developing advanced systems and methods to help you achieve the impossible.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            <FeatureCard
              icon={<Link className="h-8 sm:h-10 w-8 sm:w-10 text-[#00ffff]" />}
              title="One Team, One Mission"
              description="Consider us an extension of your team, bringing our capabilities and mission-first mindset to help you overcome your unique challenges and accomplish your objectives."
            />
            <FeatureCard
              icon={<Users className="h-8 sm:h-10 w-8 sm:w-10 text-[#ff0000]" />}
              title="Humans Before Machines"
              description="We understand most businesses can be intimidated by AI, and that's alright! That's why we embrace a Humans First approach - creating solutions to Aid and Assist human teams as opposed to replace them."
            />
            <FeatureCard
              icon={<Gauge className="h-8 sm:h-10 w-8 sm:w-10 text-[#00ffff]" />}
              title="Speed: Our Best Asset"
              description="From former web and social media experts, to robotics engineers and service industry veterans, we know what we're talking about and how to do it. Between our know how and the speed at which AI can execute, rest assured: you're in good hands!"
            />
            <FeatureCard
              icon={<TrendingUp className="h-8 sm:h-10 w-8 sm:w-10 text-[#ff0000]" />}
              title="Partners in Growth"
              description="From inception to deployment and beyond, we stand by you every step of the way. Our commitment is to your success and longevity, and that means making sure your personal team of experts is always behind you; day or night, rain or shine."
            />
          </div>
          <div className="text-center mt-12 sm:mt-16">
            <p className="text-lg sm:text-xl md:text-2xl text-white/80 mb-6 sm:mb-8 px-4">
              Join us and experience <strong>The Atlas Advantage</strong>—because your success is our success.
            </p>
            <RouterLink
              to="/services"
              className="inline-flex items-center px-8 sm:px-10 py-4 sm:py-5 bg-[#00ffff] text-white rounded-lg hover:bg-[#ff0000] transition-colors font-semibold text-lg sm:text-xl"
            >
              Our Services
              <ArrowRight className="ml-2 sm:ml-3 h-5 sm:h-6 w-5 sm:w-6" />
            </RouterLink>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-12 sm:mb-16 text-white">What Our Clients Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <TestimonialCard
              quote="Social media was a mystery to us, but Alex and Team Atlas cracked the code. Their AI-driven content strategies and automation tools helped us grow our audience, increase engagement, and attract more customers. Now, we're consistently visible and converting followers into sales. Atlas AI knows their stuff!"
              author="David Chen"
              role="Operations Director"
              image="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=200&h=200"
            />
            <TestimonialCard
              quote="Atlas AI took our business from struggling with customer demand to running like a well-oiled machine. Z introduced us to their AI-powered call system, which now answers, schedules, and routes calls seamlessly. We went from missing opportunities to handling more customers than ever before—all without added stress. If your business is growing fast, let Atlas AI help!"
              author="Sarah Johnson"
              role="Founder, Consulting"
              image="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=200&h=200"
            />
            <TestimonialCard
              quote="The website Atlas created for our restaurant chain perfectly captures our brand essence. On top of that their ACSS service is phenomenal and works wonders for us in handling orders and service requests. They really do 'get results.'"
              author="Dane Niemoller"
              role="CEO, Restaurant Industry"
              image="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=200&h=200"
            />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-24">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center text-white mb-12 sm:mb-16">Frequently Asked Questions</h2>
          <div className="space-y-4 sm:space-y-6">
            <FaqItem
              question="How can AI automation benefit my business?"
              answer="AI automation can streamline your operations, reduce costs, minimize errors, and free up your team to focus on strategic tasks. Our solutions are customized to your specific business needs."
            />
            <FaqItem
              question="What industries do you serve?"
              answer="We work with businesses across various sectors including technology, healthcare, finance, retail, and manufacturing. Our AI solutions are adaptable to any industry's specific requirements."
            />
            <FaqItem
              question="How long does implementation take?"
              answer="Implementation timelines vary based on the complexity of your needs, but typically range from 2-8 weeks. We ensure a smooth transition with minimal disruption to your operations."
            />
          </div>
          <div className="text-center mt-10 sm:mt-12">
            <RouterLink
              to="/faqs"
              className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 border-2 border-[#00ffff] text-[#00ffff] rounded-md hover:bg-[#ff0000] hover:border-[#ff0000] hover:text-white transition-colors font-semibold text-lg sm:text-xl"
            >
              More FAQs
              <ArrowRight className="ml-2 sm:ml-3 h-5 sm:h-6 w-5 sm:w-6" />
            </RouterLink>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;